import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import LegacySelect from 'react-select-legacy';
import DateRange from './DateRange';
import EditButtonSearch from './EditButtonSearch';
import FilterCheckbox from './FilterCheckbox';
import SelectCheckbox from './SelectCheckbox';
import ReportDownload from '../helpers/ReportDownload';
import { isAdminDashboard, isChatEnabled } from '../../utils/authScopes';
import { accountPlayersColumnsOptions } from '../helpers/TableHelpers';
import { groupByDisabled } from '../../utils/select';

const AccountPlayersFilter = ({
  auth, meta, onCSVEnqueue, onUserActionsCSVEnqueue, onSearch, onArchivedChange,
  accounts, onAccountChange, filterOptions, subgroups, onSubgroupChange,
  cohorts, onCohortChange, groups, onGroupChange, csvReport, userActionsCSVReport, onReportDetailsShow,
  onUserActionsReportDetailsShow, onDatesChange, options, onColumnVisibilityChange,
  partners, onPartnerChange,
  onBulkSend, onUnreadChange, isLoading }) => {

  const [q, setQ] = React.useState(meta.q);

  // Sync local state with meta.q when it changes externally (e.g., from URL or Redux)
  React.useEffect(() => {
    setQ(meta.q);
  }, [meta.q]);

  // Only call onSearch when user types (not on initial render or external updates)
  React.useEffect(() => {
    // Only call onSearch if the value actually changed from user input
    // (comparing with meta.q to avoid calling when syncing from external source)
    if (q !== meta.q) {
      onSearch(q);
    }
  }, [q, meta.q, onSearch]);

  return (
    <div>
      <div className='row u-margin-top--base u-margin-bottom--large align-middle'>
        {isAdminDashboard(auth) &&
          <div className='column small-12 medium-4 large-3 u-margin-top--base'>
            <LegacySelect
              name='sp-partner-select'
              className={'o-select o-select--optgroup'}
              multi={true}
              value={meta.partners || ''}
              onChange={onPartnerChange}
              options={partners}
              placeholder='All Partners'
              simpleValue={true}
              disabled={isLoading}
            />
          </div>}
        {groups.length > 1 &&
          <div className='column small-12 medium-4 large-3 u-margin-top--base'>
            <Select
              name='sp-group-select'
              className={'o-select o-select--optgroup'}
              isMulti
              value={groups.filter(v => (meta.groups || '').split(',').map(i => parseInt(i)).includes(v.value))}
              onChange={(v) => onGroupChange(v.map(i => i.value).join(','))}
              options={groups}
              placeholder='All Groups'
              isDisabled={isLoading}
            />
          </div>
        }
        {subgroups.length > 1 &&
          <div className='column small-12 medium-4 large-3 u-margin-top--base'>
            <Select
              name='sp-cohort-select'
              className={'o-select o-select--optgroup'}
              isMulti
              value={subgroups.filter(v => (meta.subgroups || '').split(',').map(i => parseInt(i)).includes(v.value))}
              onChange={(v) => onSubgroupChange(v.map(i => i.value).join(','))}
              options={groupByDisabled(subgroups)}
              placeholder='All Subgroups'
              isDisabled={isLoading}
            />
          </div>
        }
        {cohorts.length > 1 &&
          <div className='column small-12 medium-4 large-3 u-margin-top--base'>
            <Select
              name='sp-cohort-select'
              className={'o-select o-select--optgroup'}
              isMulti
              value={cohorts.filter(v => (meta.cohorts || '').split(',').map(i => parseInt(i)).includes(v.value))}
              onChange={(v) => onCohortChange(v.map(i => i.value).join(','))}
              options={groupByDisabled(cohorts)}
              placeholder='All Cohorts'
              isDisabled={isLoading}
            />
          </div>
        }
        <div className='column small-12 medium-4 large-3 u-margin-top--base'>
          <LegacySelect
            name='sp-account-select'
            className={'o-select o-select--optgroup'}
            filterOptions={filterOptions}
            multi={true}
            value={meta.accounts || ''}
            onChange={onAccountChange}
            options={accounts}
            placeholder='All Adults'
            simpleValue={true}
            disabled={isLoading}
          />
        </div>
        <div className='column shrink align-self-middle u-margin-top--base'>
          <FilterCheckbox
            label='Show Archived'
            value={meta.archived || false} onChange={onArchivedChange}
            disabled={isLoading}
          />
        </div>
        <div className='column shrink u-margin-top--base'>
          <SelectCheckbox
            value={options}
            options={accountPlayersColumnsOptions()}
            onChange={onColumnVisibilityChange}
            disabled={isLoading}
          />
        </div>
        <div className='column shrink u-margin-top--base'>
          <DateRange
            startDate={meta.start}
            endDate={meta.end}
            onDatesChange={onDatesChange}
            disabled={isLoading}
          />
        </div>
        <div className='column small-12 medium-4 large-3 o-filter--last u-margin-top--base'>
          <EditButtonSearch
            placeholder='Search by name...'
            value={q}
            onChange={setQ}
            disabled={isLoading}
          />
        </div>
        {isChatEnabled(auth) &&
          <div className='column small-12 u-margin-top--base'>
            <div className='row align-middle'>
              <div className='column shrink'>
                <a className='o-sp-button o-sp-button--note o-sp-button--primary' onClick={onBulkSend}>
                  Bulk Send
                </a>
              </div>
              <div className='column shrink'>
                <FilterCheckbox
                  label='Show Only Unread Messages'
                  value={meta.unread || false}
                  onChange={onUnreadChange}
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>}
      </div>
      <div className='row u-margin-bottom--small'>
        <div className='column shrink'>
          <a onClick={onCSVEnqueue}>
            <i className='fa fa-file-excel-o fa' aria-hidden="true"></i> Generate CSV report
          </a>
        </div>
        <div className='column grow'>
          <ReportDownload report={csvReport} onReportDetailsShow={onReportDetailsShow} />
        </div>
      </div>
      <div className='row u-margin-bottom--small'>
        <div className='column shrink'>
          <a onClick={onUserActionsCSVEnqueue}>
            <i className='fa fa-file-excel-o fa' aria-hidden="true"></i> Generate user actions CSV report
          </a>
        </div>
        <div className='column grow'>
          <ReportDownload report={userActionsCSVReport} onReportDetailsShow={onUserActionsReportDetailsShow} />
        </div>
      </div>
    </div>
  );
}

AccountPlayersFilter.propTypes = {
  auth: PropTypes.object.isRequired,
  meta: PropTypes.object.isRequired,
  accounts: PropTypes.arrayOf(PropTypes.object).isRequired,
  cohorts: PropTypes.arrayOf(PropTypes.object).isRequired,
  subgroups: PropTypes.arrayOf(PropTypes.object).isRequired,
  download: PropTypes.object,
  csvReport: PropTypes.object,
  userActionsCSVReport: PropTypes.object,
  groups: PropTypes.arrayOf(PropTypes.object).isRequired,
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  partners: PropTypes.arrayOf(PropTypes.object).isRequired,
  withScope: PropTypes.object.isRequired,
  filterOptions: PropTypes.func.isRequired,
  onAccountChange: PropTypes.func.isRequired,
  onArchivedChange: PropTypes.func.isRequired,
  onBulkSend: PropTypes.func.isRequired,
  onCohortChange: PropTypes.func.isRequired,
  onSubgroupChange: PropTypes.func.isRequired,
  onColumnVisibilityChange: PropTypes.func.isRequired,
  onDatesChange: PropTypes.func.isRequired,
  onGroupChange: PropTypes.func.isRequired,
  onCSVEnqueue: PropTypes.func.isRequired,
  onReportDetailsShow: PropTypes.func.isRequired,
  onUserActionsCSVEnqueue: PropTypes.func.isRequired,
  onUserActionsReportDetailsShow: PropTypes.func.isRequired,
  onPartnerChange: PropTypes.func.isRequired,
  onSearch: PropTypes.func.isRequired,
  onUnreadChange: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
}

export default AccountPlayersFilter;
